import React from 'react';
import { Palette } from 'lucide-react';

interface ColorConfig {
  primary: string;
  secondary: string;
  background: string;
  cardBackground: string;
  textPrimary: string;
  textSecondary: string;
  accent: string;
}

interface ColorPickerProps {
  colors: ColorConfig;
  onChange: (colors: ColorConfig) => void;
}

const PRESETS = [
  {
    name: 'Místico Roxo',
    desc: 'O tema original — roxo profundo com dourado',
    colors: { primary: '#8B5CF6', secondary: '#F59E0B', background: '#0F0F23', cardBackground: '#1A1A2E', textPrimary: '#FFFFFF', textSecondary: '#A0A0A0', accent: '#FF6B6B' },
  },
  {
    name: 'Índigo Cósmico',
    desc: 'Azul índigo com rosa neon — futurista e astral',
    colors: { primary: '#6366F1', secondary: '#A5B4FC', background: '#0B0B1E', cardBackground: '#131330', textPrimary: '#F0F0FF', textSecondary: '#8B8FBE', accent: '#F472B6' },
  },
  {
    name: 'Rubi Sombrio',
    desc: 'Vermelho carmim sobre fundo quase preto',
    colors: { primary: '#E11D48', secondary: '#FB7185', background: '#0F0810', cardBackground: '#1C1020', textPrimary: '#FFF0F3', textSecondary: '#FECDD3', accent: '#F59E0B' },
  },
  {
    name: 'Esmeralda Mística',
    desc: 'Verde esmeralda fundo escuro — natureza e magia',
    colors: { primary: '#10B981', secondary: '#6EE7B7', background: '#061210', cardBackground: '#0D1F1C', textPrimary: '#ECFDF5', textSecondary: '#A7F3D0', accent: '#F59E0B' },
  },
  {
    name: 'Âmbar Dourado',
    desc: 'Ouro e âmbar sobre fundo sombrio',
    colors: { primary: '#D97706', secondary: '#FCD34D', background: '#0F0C03', cardBackground: '#1C1706', textPrimary: '#FFF8E1', textSecondary: '#FDE68A', accent: '#C084FC' },
  },
];

const FIELDS: { key: keyof ColorConfig; label: string; desc: string }[] = [
  { key: 'primary',        label: 'Cor Primária',      desc: 'Botões, links e destaques principais' },
  { key: 'secondary',      label: 'Cor Secundária',    desc: 'Elementos de apoio e ícones' },
  { key: 'background',     label: 'Fundo Principal',   desc: 'Cor de fundo da página' },
  { key: 'cardBackground', label: 'Fundo dos Cards',   desc: 'Fundo das cartas e painéis' },
  { key: 'textPrimary',    label: 'Texto Principal',   desc: 'Títulos e textos de destaque' },
  { key: 'textSecondary',  label: 'Texto Secundário',  desc: 'Textos de suporte e legendas' },
  { key: 'accent',         label: 'Cor de Destaque',   desc: 'Elementos especiais e alertas' },
];

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
};

const ColorPicker: React.FC<ColorPickerProps> = ({ colors, onChange }) => {
  const handleChange = (key: keyof ColorConfig, value: string) => onChange({ ...colors, [key]: value });

  return (
    <div className="space-y-8">
      {/* Paletas */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Palette className="w-4 h-4" style={{ color: '#3b82f6' }} />
          Paletas do Site
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PRESETS.map((preset) => {
            const active = preset.colors.primary === colors.primary && preset.colors.background === colors.background;
            return (
              <button
                key={preset.name}
                onClick={() => onChange(preset.colors)}
                className="text-left rounded-xl p-4 transition-all"
                style={{
                  background: active ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.04)',
                  border: active ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {/* Color strip */}
                <div className="flex gap-1 h-6 rounded-md overflow-hidden mb-3">
                  {[preset.colors.background, preset.colors.cardBackground, preset.colors.primary, preset.colors.secondary, preset.colors.accent].map((c, i) => (
                    <div key={i} className="flex-1" style={{ background: c }} />
                  ))}
                </div>
                <div className="text-white text-sm font-medium">{preset.name}</div>
                <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{preset.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Cores individuais */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-4">Cores Personalizadas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {FIELDS.map(({ key, label, desc }) => (
            <div key={key}>
              <label className="block text-xs font-medium mb-0.5" style={{ color: '#9ca3af' }}>{label}</label>
              <p className="text-xs mb-2" style={{ color: '#6b7280' }}>{desc}</p>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg flex-shrink-0 border" style={{ background: colors[key], borderColor: 'rgba(255,255,255,0.1)' }} />
                <input
                  type="color"
                  value={colors[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="w-10 h-10 rounded-lg cursor-pointer flex-shrink-0"
                  style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'transparent' }}
                />
                <input
                  type="text"
                  value={colors[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="flex-1 px-3 py-2 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  style={inputStyle}
                  placeholder="#000000"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-3">Preview</h3>
        <div className="rounded-xl p-5" style={{ background: colors.background, border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="rounded-lg p-4" style={{ background: colors.cardBackground }}>
            <h4 className="text-base font-semibold mb-1" style={{ color: colors.textPrimary }}>Título Principal</h4>
            <p className="text-sm mb-4" style={{ color: colors.textSecondary }}>Exemplo de texto secundário com as cores selecionadas.</p>
            <div className="flex gap-2 flex-wrap">
              <button className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ background: colors.primary }}>Primário</button>
              <button className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ background: colors.secondary }}>Secundário</button>
              <button className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ background: colors.accent }}>Destaque</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
